import React, { useState, useEffect } from 'react';
import { X, Loader2, Download, AlertCircle, CheckCircle } from 'lucide-react';
import exportsAPI from '../services/exportsAPI';
import toast from 'react-hot-toast';

const ExportModal = ({ isOpen, onClose, jobId }) => {
    const [job, setJob] = useState(null);

    useEffect(() => {
        if (!isOpen || !jobId) {
            setJob(null);
            return;
        }

        const fetchStatus = async () => {
            try {
                const res = await exportsAPI.getJobStatus(jobId);
                setJob(res.data);
            } catch (err) {
                console.error(err);
                if (err.response && err.response.status === 404) {
                    // Job deleted or not found
                    onClose();
                }
            }
        };

        fetchStatus();
        const interval = setInterval(() => {
            setJob(prevJob => {
                if (prevJob && (prevJob.status === 'completed' || prevJob.status === 'failed')) {
                    clearInterval(interval);
                    return prevJob;
                }
                fetchStatus();
                return prevJob;
            });
        }, 3000);

        return () => clearInterval(interval);
    }, [isOpen, jobId, onClose]);

    if (!isOpen) return null;

    const handleCancel = async () => {
        if (!window.confirm("Voulez-vous vraiment annuler l'exportation en cours ?")) return;
        // Optimistically close modal to not freeze user UI
        onClose();
        try {
            await exportsAPI.deleteJob(jobId);
            toast.success("Exportation annulée");
        } catch (err) {
            toast.error("Erreur, le processus est peut-être déjà terminé ou verrouillé.");
        }
    };

    const handleClose = () => {
        onClose();
    };

    return (
        <div className="modal-overlay" style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
            <div className="card" style={{ width: '450px', maxWidth: '90%', padding: '24px', position: 'relative' }}>
                <button onClick={handleClose} disabled={job?.status === 'processing' || job?.status === 'pending'} style={{
                    position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', cursor: 'pointer',
                    opacity: (job?.status === 'processing' || job?.status === 'pending') ? 0.3 : 1
                }}>
                    <X size={20} />
                </button>

                <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px', color: 'var(--primary-800)' }}>
                    Exportation du Dossier
                </h3>

                {!job ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '30px' }}>
                        <Loader2 size={30} className="spinner" color="var(--primary-500)" />
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: '600', fontSize: '14px' }}>Statut :</span>
                            <span>
                                {job.status === 'pending' && <span className="badge badge-gray">En attente</span>}
                                {job.status === 'processing' && <span className="badge badge-blue" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Loader2 size={12} className="spinner" /> En cours</span>}
                                {job.status === 'completed' && <span className="badge badge-green" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><CheckCircle size={12} /> Terminé</span>}
                                {job.status === 'failed' && <span className="badge badge-red" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><AlertCircle size={12} /> Échoué</span>}
                            </span>
                        </div>

                        <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
                                <span>Progression</span>
                                <span>{job.progress}%</span>
                            </div>
                            <div style={{ width: '100%', height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                                <div style={{
                                    height: '100%', width: `${job.progress}%`,
                                    background: job.status === 'failed' ? 'var(--danger)' : 'var(--primary-500)',
                                    transition: 'width 0.4s ease'
                                }} />
                            </div>
                            {job.status === 'processing' && (
                                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '8px', textAlign: 'center' }}>
                                    Préparation des fichiers et génération des PDF...
                                </div>
                            )}
                            {job.status === 'failed' && (
                                <div style={{ fontSize: '12px', color: 'var(--danger)', marginTop: '8px', textAlign: 'center' }}>
                                    {job.error_message}
                                </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '10px' }}>
                            {(job.status === 'pending' || job.status === 'processing') ? (
                                <button className="btn btn-ghost" onClick={handleCancel} style={{ color: 'var(--danger)' }}>
                                    Annuler l'export
                                </button>
                            ) : (
                                <button className="btn btn-ghost" onClick={handleClose}>
                                    Fermer
                                </button>
                            )}

                            {job.status === 'completed' && (
                                <a
                                    href={exportsAPI.downloadUrl(job.id)}
                                    className="btn btn-primary"
                                    download
                                >
                                    <Download size={16} /> Télécharger le ZIP
                                </a>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExportModal;
